import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, Lightbulb, LayoutList,
  BookOpen, Trophy, ScanText, Download, Loader2,
} from "lucide-react";

const EXAM_LABELS = { upsc: "UPSC", state_psc: "State PSC", generic: "Generic" };
const GRADE_COLORS = {
  Excellent: "#2D6A4F",
  Good: "#3B8266",
  Average: "#D97757",
  "Needs Improvement": "#C0492B",
};

function ScoreRing({ score, max }) {
  const pct = Math.max(0, Math.min(100, (Number(score) / Number(max || 10)) * 100));
  const r = 54;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-40 h-40">
      <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#EAF1ED" strokeWidth="10" />
        <circle
          cx="60" cy="60" r={r} fill="none" stroke="#2D6A4F" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl text-[#2D6A4F] leading-none">{score}</span>
        <span className="text-sm text-[#525252] mt-1">/ {max}</span>
      </div>
    </div>
  );
}

function ListBlock({ title, Icon, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5" style={{ color }} />
        <h3 className="font-semibold text-lg">{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 text-[#333]">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Result() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [imgUrls, setImgUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const created = [];
    let timer;
    let cancelled = false;
    let imagesLoaded = false;

    const loadImages = async (data) => {
      if (imagesLoaded) return;
      imagesLoaded = true;
      const paths = data.storage_paths?.length ? data.storage_paths : (data.storage_path ? [data.storage_path] : []);
      const urls = [];
      for (const p of paths) {
        try {
          const res = await api.get(`/files/${p}`, { responseType: "blob" });
          const u = URL.createObjectURL(res.data);
          urls.push(u); created.push(u);
        } catch { /* ignore */ }
      }
      if (!cancelled) setImgUrls(urls);
    };

    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/evaluations/${id}`);
        if (cancelled) return;
        setItem(data);
        setLoading(false);
        loadImages(data);
        if (data.status === "processing") {
          timer = setTimeout(fetchOnce, 3000);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOnce();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [id]);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/evaluations/${id}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mainsmentor-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDF9]">
        <Navbar />
        <div className="max-w-6xl mx-auto px-5 py-20 text-[#525252]">Loading evaluation…</div>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="min-h-screen bg-[#FDFDF9]">
        <Navbar />
        <div className="max-w-6xl mx-auto px-5 py-20">Evaluation not found.</div>
      </div>
    );
  }

  if (item.status === "processing") {
    return (
      <div className="min-h-screen bg-[#FDFDF9]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="result-processing">
          <div className="w-16 h-16 rounded-2xl bg-[#EAF1ED] flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-7 h-7 text-[#2D6A4F] animate-spin" />
          </div>
          <h1 className="font-display text-3xl tracking-tight">Your mentor is reading your answer…</h1>
          <p className="text-[#525252] mt-3">This usually takes 30–60 seconds. Stay on this page — it updates automatically.</p>
          <p className="text-sm text-[#525252] mt-4 max-w-xl mx-auto">{item.question ? `Q: ${item.question}` : "Reading the question from the top of your first page…"}</p>
        </main>
      </div>
    );
  }

  if (item.status === "failed") {
    return (
      <div className="min-h-screen bg-[#FDFDF9]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="result-failed">
          <div className="w-16 h-16 rounded-2xl bg-[#FBEEE8] flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-7 h-7 text-[#C0492B]" />
          </div>
          <h1 className="font-display text-3xl tracking-tight">Evaluation couldn't be completed</h1>
          <p className="text-[#525252] mt-3">{item.error || "Something went wrong. Your credit has been refunded."}</p>
          <Link to="/evaluate" className="inline-block mt-8">
            <button className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white h-12 px-7 font-medium transition-colors">Try again</button>
          </Link>
        </main>
      </div>
    );
  }

  const r = item.result || {};
  const grade = r.grade || "—";

  return (
    <div className="min-h-screen bg-[#FDFDF9]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 py-8" data-testid="result-page">
        <div className="flex items-center justify-between mb-6">
          <Link to="/history" className="inline-flex items-center gap-2 text-sm text-[#525252] hover:text-[#2D6A4F]">
            <ArrowLeft className="w-4 h-4" /> Back to history
          </Link>
          <button
            data-testid="download-report-btn"
            onClick={downloadReport}
            disabled={downloading}
            className="inline-flex items-center gap-2 rounded-full border border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#EAF1ED] px-4 py-2 text-sm font-semibold transition-colors"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF report
          </button>
        </div>

        {/* Header card */}
        <div className="bg-white rounded-3xl border border-[#E5E5E5] p-6 sm:p-8 grid md:grid-cols-[auto,1fr] gap-8 items-center">
          <div className="flex justify-center"><ScoreRing score={r.score} max={r.max_marks || item.max_marks} /></div>
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#2D6A4F] bg-[#EAF1ED] px-2.5 py-1 rounded-full">
                {EXAM_LABELS[item.exam_type] || item.exam_type}
              </span>
              {item.subject && <span className="text-xs text-[#525252] bg-[#F3F3ED] px-2.5 py-1 rounded-full">{item.subject}</span>}
              <span
                data-testid="grade-badge"
                className="text-xs uppercase tracking-[0.15em] font-semibold px-2.5 py-1 rounded-full text-white"
                style={{ background: GRADE_COLORS[grade] || "#525252" }}
              >
                {grade}
              </span>
            </div>
            <h1 className="font-display text-2xl sm:text-3xl tracking-tight leading-snug">{item.question}</h1>
            <p className="mt-3 text-[#525252]">{r.summary}</p>
          </div>
        </div>

        {/* Dimensions */}
        {Array.isArray(r.dimensions) && r.dimensions.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            {r.dimensions.map((d, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E5E5E5] p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">{d.name}</span>
                  <span className="font-display text-xl text-[#2D6A4F]">{d.score}<span className="text-sm text-[#525252]">/10</span></span>
                </div>
                <Progress value={(Number(d.score) || 0) * 10} className="h-2" />
                <p className="text-sm text-[#525252] mt-3">{d.comment}</p>
              </div>
            ))}
          </div>
        )}

        {/* Feedback lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
          <ListBlock title="Strengths" Icon={CheckCircle2} items={r.strengths} color="#2D6A4F" />
          <ListBlock title="Weaknesses" Icon={AlertTriangle} items={r.weaknesses} color="#C0492B" />
          <ListBlock title="How to improve" Icon={Lightbulb} items={r.improvements} color="#D97757" />
          <ListBlock title="Structure & presentation tips" Icon={LayoutList} items={r.structure_tips} color="#2D6A4F" />
        </div>

        {/* Tabs: model answer / topper / your copy */}
        <div className="mt-6 bg-white rounded-2xl border border-[#E5E5E5] p-6">
          <Tabs defaultValue="model">
            <TabsList className="bg-[#F3F3ED]">
              <TabsTrigger value="model" data-testid="tab-model"><BookOpen className="w-4 h-4 mr-1.5" /> Model answer</TabsTrigger>
              <TabsTrigger value="topper" data-testid="tab-topper"><Trophy className="w-4 h-4 mr-1.5" /> Topper comparison</TabsTrigger>
              <TabsTrigger value="copy" data-testid="tab-copy"><ScanText className="w-4 h-4 mr-1.5" /> Your copy</TabsTrigger>
            </TabsList>
            <TabsContent value="model" className="mt-5">
              <p className="whitespace-pre-wrap leading-relaxed text-[#333]">{r.model_answer}</p>
            </TabsContent>
            <TabsContent value="topper" className="mt-5">
              <p className="whitespace-pre-wrap leading-relaxed text-[#333]">{r.topper_comparison}</p>
            </TabsContent>
            <TabsContent value="copy" className="mt-5">
              <div className="space-y-3">
                {imgUrls.length === 0 && <p className="text-sm text-[#525252]">Image not available.</p>}
                {imgUrls.map((u, i) => (
                  <div key={i}>
                    {imgUrls.length > 1 && <span className="text-xs uppercase tracking-[0.15em] text-[#525252]">Page {i + 1}</span>}
                    <img src={u} alt={`Answer page ${i + 1}`} className="mt-1 rounded-xl border border-[#E5E5E5] w-full object-contain max-h-[500px] bg-[#FAFAF5]" />
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-8 flex justify-center">
          <Link to="/evaluate">
            <button className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white h-12 px-7 font-medium transition-colors">
              Evaluate another answer
            </button>
          </Link>
        </div>
      </main>
    </div>
  );
}
